import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { formatLocal } from "@/lib/timezone";
import { Robot, Coins, ChartBar, Warning, CurrencyDollar, ArrowsClockwise, Bell, FloppyDisk, PaperPlaneTilt } from "@phosphor-icons/react";

/* ─────────── helpers ─────────── */
const fmtUSD = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtInt = (n) => (n || 0).toLocaleString();
const MONTH_LABEL = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "short", year: "numeric" });
};const PROVIDER_COLOR = { anthropic: "#B45309", openai: "#047857" };
const PROVIDER_LABEL = { anthropic: "Claude (Anthropic)", openai: "OpenAI" };

/* ─────────── stat tile ─────────── */
function StatTile({ label, value, sub, accent = "#0055FF", icon, testId }) {
  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5 flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2" style={{ color: accent }}>
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-[0.15em]">{label}</span>
      </div>
      <span className="text-3xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value}
      </span>
      {sub && <span className="text-xs text-[#A3A3A3] mt-1">{sub}</span>}
    </Card>
  );
}

/* ─────────── budget panel ─────────── */
function LlmBudgetPanel({ onBudgetChange }) {
  const [budget, setBudget] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/llm-usage/budget");
      setBudget(r.data);
      setDraft(String(r.data.budget_usd || 0));
    } catch { toast.error("Failed to load budget"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const val = parseFloat(draft);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid number ≥ 0"); return; }
    setSaving(true);
    try {
      await api.put("/admin/llm-usage/budget", { budget_usd: val });
      toast.success(val === 0 ? "Budget alert disabled" : `Budget set to $${val.toLocaleString()}/month`);
      await load();
      onBudgetChange && onBudgetChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const testAlert = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/llm-usage/budget/test-alert");
      toast.success(`Test SMS sent to ${r.data.sent} of ${r.data.recipients.length} subscribed number(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    setTesting(false);
  };

  if (!budget) return null;

  const pct = budget.percent_used || 0;
  const barColor = pct >= 100 ? "#DC2626" : pct >= 80 ? "#D97706" : "#16A34A";
  const barWidth = Math.min(100, pct);

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="llm-budget-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252] flex items-center gap-1.5">
            <Bell size={12} /> Monthly Budget Alert
          </h3>
          <p className="text-[11px] text-[#A3A3A3] mt-0.5">
            Fires a Twilio SMS at 80 % and 100 % of the cap to every user subscribed to <code>llm_budget_breach</code> in the SMS Routing Matrix. Set to 0 to disable.
          </p>
        </div>
        <button onClick={testAlert} disabled={testing || !budget.enabled}
                className="h-8 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40 flex items-center gap-1.5"
                data-testid="test-budget-alert">
          <PaperPlaneTilt size={12} />{testing ? "Sending…" : "Test Alert"}
        </button>
      </div>

      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-1">Monthly cap (USD)</label>
          <div className="flex items-center">
            <span className="h-9 px-2 border border-[#E5E5E5] border-r-0 bg-[#FAFAFA] text-sm text-[#525252] flex items-center">$</span>
            <input
              type="number" step="1" min="0"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              className="h-9 w-32 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF] tabular-nums"
              data-testid="budget-input"
            />
            <span className="h-9 px-2 border border-[#E5E5E5] border-l-0 bg-[#FAFAFA] text-xs text-[#A3A3A3] flex items-center">/ month</span>
          </div>
        </div>
        <button onClick={save} disabled={saving || draft === String(budget.budget_usd)}
                className="h-9 px-4 bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5"
                data-testid="save-budget">
          <FloppyDisk size={12} />{saving ? "Saving…" : "Save"}
        </button>
      </div>

      {budget.enabled && (
        <>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#525252]">Month-to-date spend</span>
            <span className="font-bold tabular-nums" style={{ color: barColor }}>
              ${budget.month_to_date_spend.toFixed(2)} of ${budget.budget_usd.toLocaleString()} &middot; {pct.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 bg-[#F5F5F5] w-full">
            <div
              className="h-full transition-all"
              style={{ width: `${barWidth}%`, backgroundColor: barColor }}
              data-testid="budget-progress-bar"
            />
          </div>
          {pct >= 80 && pct < 100 && (
            <p className="text-[11px] text-[#D97706] mt-2 font-bold flex items-center gap-1">
              <Warning size={12} weight="fill" /> Warning threshold (80 %) reached — alert sent.
            </p>
          )}
          {pct >= 100 && (
            <p className="text-[11px] text-[#DC2626] mt-2 font-bold flex items-center gap-1">
              <Warning size={12} weight="fill" /> Critical threshold (100 %) reached — alert sent.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

/* ─────────── main tab ─────────── */
export default function LlmUsageTab() {
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [features, setFeatures] = useState([]);
  const [recent, setRecent] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterFeature, setFilterFeature] = useState("");
  const [filterProvider, setFilterProvider] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, m, f, p] = await Promise.all([
        api.get("/admin/llm-usage/summary"),
        api.get("/admin/llm-usage/monthly?months=12"),
        api.get("/admin/llm-usage/by-feature?days=30"),
        api.get("/admin/llm-usage/pricing"),
      ]);
      setSummary(s.data);
      setMonthly(m.data);
      setFeatures(f.data || []);
      setPricing(p.data);
    } catch (e) {
      toast.error("Failed to load LLM usage");
    }
    setLoading(false);
  };

  const loadRecent = async () => {
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (filterFeature) params.set("feature", filterFeature);
      if (filterProvider) params.set("provider", filterProvider);
      if (onlyErrors) params.set("only_errors", "true");
      const r = await api.get(`/admin/llm-usage/recent?${params.toString()}`);
      setRecent(r.data || []);
    } catch { toast.error("Failed to load recent calls"); }
  };

  useEffect(() => { loadAll(); loadRecent(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadRecent(); /* eslint-disable-next-line */ }, [filterFeature, filterProvider, onlyErrors]);

  if (loading) {
    return (
      <div className="p-8 flex justify-center" data-testid="llm-usage-loading">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  const mtd = summary?.month_to_date || {};
  const d30 = summary?.last_30_days || {};
  const lifetime = summary?.lifetime || {};
  const allFeatures = Array.from(new Set([
    ...features.map(x => x.feature),
    ...recent.map(x => x.feature),
  ])).sort();

  return (
    <div className="space-y-6" data-testid="llm-usage-tab">
      {/* ─── Header + reload ─── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
            <Robot size={14} /> LLM Usage & Cost Tracking
          </h2>
          <p className="text-[11px] text-[#525252] mt-0.5">
            Per-call log for every request to Anthropic Claude and OpenAI. Costs are estimated from the public price sheets.
          </p>
        </div>
        <button onClick={() => { loadAll(); loadRecent(); }} className="h-8 px-3 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider text-[#525252] flex items-center gap-1.5" data-testid="llm-usage-reload">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {/* ─── Monthly budget + SMS alert ─── */}
      <LlmBudgetPanel onBudgetChange={loadAll} />

      {/* ─── Top stats ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Month-to-date spend"
          value={fmtUSD(mtd.cost_usd)}
          sub={`${fmtInt(mtd.calls)} calls · ${fmtInt(mtd.total_tokens)} tokens`}
          accent="#0055FF"
          icon={<CurrencyDollar size={16} weight="duotone" />}
          testId="stat-mtd"
        />
        <StatTile
          label="Last 30 days"
          value={fmtUSD(d30.cost_usd)}
          sub={`${fmtInt(d30.calls)} calls · avg ${Math.round(d30.avg_latency_ms || 0)}ms`}
          accent="#7C3AED"
          icon={<ChartBar size={16} weight="duotone" />}
          testId="stat-30d"
        />
        <StatTile
          label="Lifetime"
          value={fmtUSD(lifetime.cost_usd)}
          sub={`${fmtInt(lifetime.calls)} calls · ${fmtInt(lifetime.total_tokens)} tokens`}
          accent="#16A34A"
          icon={<Coins size={16} weight="duotone" />}
          testId="stat-lifetime"
        />
        <StatTile
          label="Error rate (30d)"
          value={`${(summary?.error_rate_30d_pct || 0).toFixed(2)}%`}
          sub={summary?.error_rate_30d_pct > 1 ? "Above 1% — investigate" : "Healthy"}
          accent={summary?.error_rate_30d_pct > 1 ? "#DC2626" : "#525252"}
          icon={<Warning size={16} weight="duotone" />}
          testId="stat-error-rate"
        />
      </div>

      {/* ─── Monthly summary table ─── */}
      <Card className="border border-[#E5E5E5] rounded-none p-0" data-testid="monthly-summary-card">
        <div className="px-5 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Monthly Summary · last 12 months</h3>
        </div>
        {(monthly?.by_month || []).length === 0 ? (
          <p className="p-10 text-center text-sm text-[#A3A3A3]">No usage recorded yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="monthly-summary-table">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Month</th>
                <th className="text-right px-3 py-2 font-bold">Calls</th>
                <th className="text-right px-3 py-2 font-bold">Input tokens</th>
                <th className="text-right px-3 py-2 font-bold">Output tokens</th>
                <th className="text-right px-3 py-2 font-bold">Claude cost</th>
                <th className="text-right px-3 py-2 font-bold">OpenAI cost</th>
                <th className="text-right px-5 py-2 font-bold">Total cost</th>
              </tr>
            </thead>
            <tbody>
              {monthly.by_month.map(row => {
                const perProvider = monthly.by_month_provider.filter(p => p.month === row.month);
                const claudeCost = perProvider.filter(p => p.provider === "anthropic").reduce((a, b) => a + b.cost_usd, 0);
                const openaiCost = perProvider.filter(p => p.provider === "openai").reduce((a, b) => a + b.cost_usd, 0);
                return (
                  <tr key={row.month} className="border-t border-[#F5F5F5]" data-testid={`month-row-${row.month}`}>
                    <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{MONTH_LABEL(row.month)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtInt(row.calls)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-[#525252]">{fmtInt(row.input_tokens)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-[#525252]">{fmtInt(row.output_tokens)}</td>
                    <td className="px-3 py-3 text-right tabular-nums" style={{ color: PROVIDER_COLOR.anthropic }}>{fmtUSD(claudeCost)}</td>
                    <td className="px-3 py-3 text-right tabular-nums" style={{ color: PROVIDER_COLOR.openai }}>{fmtUSD(openaiCost)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-bold text-[#0A0A0A]">{fmtUSD(row.cost_usd)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* ─── Top features by cost (30d) ─── */}
      <Card className="border border-[#E5E5E5] rounded-none p-0" data-testid="features-card">
        <div className="px-5 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">By Feature · last 30 days</h3>
        </div>
        {features.length === 0 ? (
          <p className="p-10 text-center text-sm text-[#A3A3A3]">No feature usage in the last 30 days.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Feature</th>
                <th className="text-left px-3 py-2 font-bold">Provider</th>
                <th className="text-right px-3 py-2 font-bold">Calls</th>
                <th className="text-right px-3 py-2 font-bold">Tokens</th>
                <th className="text-right px-3 py-2 font-bold">Avg latency</th>
                <th className="text-right px-5 py-2 font-bold">Cost</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f, i) => (
                <tr key={i} className="border-t border-[#F5F5F5]" data-testid={`feature-row-${f.feature}-${f.provider}`}>
                  <td className="px-5 py-3 font-mono text-xs text-[#0A0A0A]">{f.feature}</td>
                  <td className="px-3 py-3">
                    <span className="px-1.5 py-0.5 text-[10px] font-bold" style={{ color: PROVIDER_COLOR[f.provider], backgroundColor: `${PROVIDER_COLOR[f.provider]}14` }}>
                      {PROVIDER_LABEL[f.provider] || f.provider}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{fmtInt(f.calls)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#525252]">{fmtInt(f.tokens)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#525252]">{Math.round(f.avg_latency_ms || 0)}ms</td>
                  <td className="px-5 py-3 text-right tabular-nums font-bold">{fmtUSD(f.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ─── Recent calls ─── */}
      <Card className="border border-[#E5E5E5] rounded-none p-0" data-testid="recent-calls-card">
        <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Recent Calls</h3>
          <div className="flex items-center gap-2">
            <select value={filterFeature} onChange={e => setFilterFeature(e.target.value)} className="h-8 px-2 text-xs border border-[#E5E5E5] focus:outline-none" data-testid="filter-feature">
              <option value="">All features</option>
              {allFeatures.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={filterProvider} onChange={e => setFilterProvider(e.target.value)} className="h-8 px-2 text-xs border border-[#E5E5E5] focus:outline-none" data-testid="filter-provider">
              <option value="">All providers</option>
              <option value="anthropic">Claude</option>
              <option value="openai">OpenAI</option>
            </select>
            <label className="flex items-center gap-1 text-xs text-[#525252]">
              <input type="checkbox" checked={onlyErrors} onChange={e => setOnlyErrors(e.target.checked)} data-testid="filter-errors" />
              Errors only
            </label>
          </div>
        </div>
        {recent.length === 0 ? (
          <p className="p-10 text-center text-sm text-[#A3A3A3]">No calls match the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-5 py-2 font-bold">When</th>
                  <th className="text-left px-3 py-2 font-bold">User</th>
                  <th className="text-left px-3 py-2 font-bold">Feature</th>
                  <th className="text-left px-3 py-2 font-bold">Model</th>
                  <th className="text-right px-3 py-2 font-bold">In</th>
                  <th className="text-right px-3 py-2 font-bold">Out</th>
                  <th className="text-right px-3 py-2 font-bold">Latency</th>
                  <th className="text-right px-5 py-2 font-bold">Cost</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.id} className={`border-t border-[#F5F5F5] ${!r.success ? "bg-[#FEF2F2]" : ""}`} data-testid={`recent-row-${r.id}`}>
                    <td className="px-5 py-2 text-xs text-[#525252] tabular-nums" title={`UTC: ${r.created_at}`}>{formatLocal(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs text-[#525252] truncate max-w-[160px]" title={r.user_email || ""}>{r.user_email || <span className="italic text-[#A3A3A3]">system</span>}</td>
                    <td className="px-3 py-2 text-xs font-mono text-[#0A0A0A]" title={r.error_message || ""}>
                      {r.feature}
                      {!r.success && <span className="ml-1 text-[#DC2626] font-bold">✗</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#525252] font-mono">{r.model}</td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtInt(r.input_tokens)}</td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtInt(r.output_tokens)}</td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-[#525252]">{r.latency_ms}ms</td>
                    <td className="px-5 py-2 text-right text-xs tabular-nums font-semibold">{fmtUSD(r.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ─── Pricing reference ─── */}
      {pricing && (
        <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="pricing-card">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252] mb-3">Current Pricing Reference (USD per 1M tokens)</h3>
          <p className="text-[11px] text-[#A3A3A3] mb-3">{pricing.note}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-xs">
            {Object.entries(pricing.rates).map(([model, r]) => (
              <div key={model} className="flex items-center justify-between border-b border-[#F5F5F5] py-1 font-mono">
                <span className="text-[#525252]">{model}</span>
                <span className="text-[#0A0A0A] tabular-nums">
                  {r.audio_per_minute
                    ? `$${r.audio_per_minute}/min`
                    : `in $${r.input} · out $${r.output}`}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
